import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import Konva from 'konva';
import { Stage, Layer, Image as KonvaImage, Group, Transformer, Line, Rect, Circle } from 'react-konva';
import { useTransformer } from './TransformerContext';
import Arrows from "./Arrows";
import TextBox from './TextBox';
import Callout from './Callout';
import Freehand from './Freehand';
import BasicShapes from './BasicShapes';


const applyFilters = (node, filters) => {
  if (!node) return;
  node.cache(); // cache is required
  const konvaFilters = [];

  // Apply each filter based on the slider values
  if (filters.blur > 0) {
    konvaFilters.push(Konva.Filters.Blur);
    node.blurRadius(filters.blur);
  }

  if (filters.brightness !== 100) {
    konvaFilters.push(Konva.Filters.Brighten);
    node.brightness((filters.brightness - 100) / 100); // normalize
  }

  if (filters.contrast !== 100) {
    konvaFilters.push(Konva.Filters.Contrast);
    node.contrast((filters.contrast - 100) / 100); // normalize
  }

  if (filters.grayscale > 0) {
    konvaFilters.push(Konva.Filters.Grayscale);
  }

  if (filters.huerotate > 0) {
    konvaFilters.push(Konva.Filters.HSL);
    node.hue(filters.huerotate);
  }

  if (filters.invert > 0) {
    konvaFilters.push(Konva.Filters.Invert);
  }

  if (filters.opacity !== 100) {
    konvaFilters.push(Konva.Filters.RGBA);
    node.alpha(filters.opacity / 100); // 0-1
  }

  if (filters.saturate !== 100) {
    konvaFilters.push(Konva.Filters.HSL);
    node.saturation((filters.saturate - 100) / 100);
  }

  if (filters.sepia > 0) {
    konvaFilters.push(Konva.Filters.Sepia);
  }

  node.filters(konvaFilters);
  node.getLayer().batchDraw();
};


const Canvas = forwardRef(({ clear, type, setShapeType, shapes, setShapes, filters,
  selectedShape, download = false, onSelect = () => { }, selectedIds, imgSrc, trigerRef,
  onSave, markups, downloadName = new Date(), setTool }) => {
  const containerRef = useRef();
  const stageRef = useRef();
  const layerRef = useRef();
  const transformerRef = useTransformer();
  const imageRef = useRef();

  const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [bgImage, setBgImage] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [liveLine, setLiveLine] = useState([]);
  const pointsRef = useRef([]); // Ref to avoid rerender on every point update
  const [selectionBox, setSelectionBox] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const startPosRef = useRef(null);
  const moveHandleRef = useRef();
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scaled, setScaled] = useState({ width: 0, height: 0 });
  const coordsRef = useRef({ x: 0, y: 0 });
  const rafRef = useRef(null);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    const resize = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // 🔹 Load Image
  useEffect(() => {
    if (!imgSrc || imgSrc === "undefined" || imgSrc === "null" || (typeof imgSrc === "string" && (imgSrc.endsWith("undefined") || imgSrc.endsWith("null")))) return;
    const image = new window.Image();

    image.crossOrigin = "anonymous"; // ✅ MUST be set first

    image.onload = () => {
      setBgImage(image);

      const maxWidth = window.innerWidth;
      const maxHeight = window.innerHeight * 0.74;

      const ratio = Math.min(
        maxWidth / image.width,
        maxHeight / image.height,
        1
      );

      setScaled({
        width: image.width * ratio,
        height: image.height * ratio
      });
    };

    image.src = imgSrc; // ✅ set last
  }, [imgSrc]);

  // 🔹 Adjust scaled canvas dimensions when resized
  useEffect(() => {
    if (!bgImage) return;
    const maxWidth = window.innerWidth;
    const maxHeight = window.innerHeight * 0.74;

    const ratio = Math.min(
      maxWidth / bgImage.width,
      maxHeight / bgImage.height,
      1
    );

    setScaled({
      width: bgImage.width * ratio,
      height: bgImage.height * ratio
    });
  }, [dimensions, bgImage]);

  const clamp = (value, min, max) => {
    return Math.max(min, Math.min(max, value));
  };

  const moveStage = (dx, dy) => {
    const stage = imageRef.current;
    if (!stage) return
    const scale = stage.scaleX();
    const stageWidth = stage.width();
    const stageHeight = stage.height();

    const imageWidth = imageRef.current.width() * scale;
    const imageHeight = imageRef.current.height() * scale;

    // 👉 Calculate limits
    const minX = Math.min(0, stageWidth - imageWidth);
    const maxX = 0;

    const minY = Math.min(0, stageHeight - imageHeight);
    const maxY = 0;

    // 👉 Apply movement
    let newX = stage.x() + dx;
    let newY = stage.y() + dy;

    // 👉 Clamp it
    newX = clamp(newX, minX, maxX);
    newY = clamp(newY, minY, maxY);

    stage.to({
      x: newX,
      y: newY,
      duration: 0.1,
    });
  };

  const zoomStage = (direction) => {
    const stage = imageRef.current;
    const scaleBy = 1.1;
    if (!stage) return
    const oldScale = stage.scaleX();

    const newScale =
      direction === "in"
        ? oldScale * scaleBy
        : oldScale / scaleBy;

    // 👉 Get center of stage
    const center = {
      x: stage.width() / 2,
      y: stage.height() / 2,
    };

    // 👉 Calculate new position to keep center fixed
    const mousePointTo = {
      x: (center.x - stage.x()) / oldScale,
      y: (center.y - stage.y()) / oldScale,
    };

    const newPos = {
      x: center.x - mousePointTo.x * newScale,
      y: center.y - mousePointTo.y * newScale,
    };

    stage.to({
      scaleX: newScale,
      scaleY: newScale,
      x: newPos.x,
      y: newPos.y,
      duration: 0.2,
    });
  };
  // Calculate scaled image size (preserving aspect ratio)
  const getScaledImageSize = (img, container) => {
    if (!img || !container.width || !container.height) return { width: 0, height: 0 };

    const imgRatio = img.width / img.height;
    const containerRatio = container.width / container.height;
    let width, height;

    if (imgRatio > containerRatio) {
      // Image is wider → fit to width
      width = container.width;
      height = container.width / imgRatio;
    } else {
      // Image is taller → fit to height
      height = container.height;
      width = container.height * imgRatio;
    }

    return { width, height };
  };

  // const scaled = getScaledImageSize(bgImage, dimensions);

  useEffect(() => {
    if (clear == null) onSelect(null);
  }, [clear]);

  useEffect(() => {
    if (download) handleDownload(download);
  }, [download]);

  useEffect(() => {
    applyFilters(imageRef.current, filters)
  }, [filters]);

  const handlePrint = (dataUrl, downloadName) => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
    <html>
      <head>
        <title>${downloadName}</title>
        <style>
          @page {
            size: auto;
            margin: 0mm;
          }
          body {
            margin: 0;
            padding: 0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          img {
            width: 100%;
            max-width: 100%;
            height: auto;
            display: block;
            object-fit: contain;
          }
        </style>
      </head>
      <body>
        <img src="${dataUrl}" />
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 100);
          };
        </script>
      </body>
    </html>
  `);
    printWindow.document.close();
  };

  const handleDownload = (download) => {
    if (!download) return
    const attachedNodes = transformerRef?.current?.nodes();
    transformerRef.current?.nodes([])
    const tempSelectedIds = selectedIds
    onSelect([])
    try {
      const uri = stageRef.current?.toDataURL({ pixelRatio: 2 });
      if (download == "export") {
        const link = document.createElement('a');
        link.download = downloadName + '.png';
        link.href = uri;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      if (download == "print") {
        handlePrint(uri, downloadName)
      }
      if (download == "save" || download == "share") {
        onSave(uri, download)
      }
      transformerRef.current?.nodes(attachedNodes)
      onSelect(tempSelectedIds)
    } catch (e) {
      console.log("Error")
    }
  };

  const handleMouseDown = (e) => {
    const shape = shapes.find(shape => selectedIds.includes(shape.id));
    if (shape?.props?.drawing && shape?.type === 'freehand') {
      const pos = e.target.getStage().getPointerPosition();
      pointsRef.current = [pos.x, pos.y];
      setLiveLine([pos.x, pos.y]);
      setDrawing(true);
    } else if (["rectangle"].includes(shape?.type)) {
      const pos = e.target.getStage().getPointerPosition();
      pointsRef.current = [pos.x, pos.y];
      setLiveLine([pos.x, pos.y]);
      // setDrawing(true);
    } else if (type == "selection") {
      const { x, y } = e.target.getStage().getPointerPosition();
      startPosRef.current = { x, y };
      setSelectionBox({ x, y, width: 0, height: 0 });
    }

  };

  const updatePointerCoords = (e) => {
    if (!setTool) return;
    const stage = e?.target?.getStage();
    if (!stage) return;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    let imgX = pointer.x;
    let imgY = pointer.y;
    if (imageRef.current) {
      const transform = imageRef.current.getAbsoluteTransform().copy().invert();
      const localPos = transform.point(pointer);
      imgX = localPos.x;
      imgY = localPos.y;
    }

    const imgHeight = scaled.height > 6 ? scaled.height - 6 : scaled.height;
    if (imgX >= 0 && imgX <= scaled.width && imgY >= 0 && imgY <= imgHeight) {
      const nextX = Number(imgX.toFixed(2));
      const nextY = Number(imgY.toFixed(2));
      if (nextX !== coordsRef.current.x || nextY !== coordsRef.current.y) {
        coordsRef.current = { x: nextX, y: nextY };
        if (!rafRef.current) {
          rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            setTool((prev) => ({
              ...prev,
              x: coordsRef.current.x,
              y: coordsRef.current.y
            }));
          });
        }
      }
    } else {
      if (coordsRef.current.x !== 0 || coordsRef.current.y !== 0) {
        coordsRef.current = { x: 0, y: 0 };
        if (!rafRef.current) {
          rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            setTool((prev) => ({ ...prev, x: 0, y: 0 }));
          });
        }
      }
    }
  };

  const handleMouseLeave = () => {
    if (coordsRef.current.x !== 0 || coordsRef.current.y !== 0) {
      coordsRef.current = { x: 0, y: 0 };
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (setTool) {
        setTool((prev) => ({ ...prev, x: 0, y: 0 }));
      }
    }
  };

  const handleMouseMove = (e) => {
    updatePointerCoords(e);

    if (type == "selection") {
      if (!startPosRef.current) return;
      const { x, y } = e.target.getStage().getPointerPosition();
      const sx = startPosRef.current.x;
      const sy = startPosRef.current.y;

      setSelectionBox({
        x: Math.min(x, sx),
        y: Math.min(y, sy),
        width: Math.abs(x - sx),
        height: Math.abs(y - sy),
      });

    }

    if (!drawing) return;
    const pos = e.target.getStage().getPointerPosition();
    pointsRef.current.push(pos.x, pos.y);
    setLiveLine([...pointsRef.current]); // still batched with React, but less frequent
  };

  const handleMouseUp = () => {
    if (type == 'selection') {
      if (!selectionBox) return;
      if (!transformerRef.current) return;

      const box = selectionBox;
      const selected = [];
      const selectedIdlist = [];
      layerRef.current.getChildren().forEach((node) => {
        if (node) {
          const attrs = node.getAttrs();
          if (node.getClassName() !== 'Transformer' && !attrs.skiable) {

            // console.log("attrs", attrs.id)
            const boxRect = node.getClientRect();
            const isInside =
              boxRect.x >= box.x &&
              boxRect.y >= box.y &&
              boxRect.x + boxRect.width <= box.x + box.width &&
              boxRect.y + boxRect.height <= box.y + box.height;
            if (isInside) { selected.push(node); selectedIdlist.push(attrs.id) }
          }
        }
      });
      transformerRef.current.nodes([])
      setSelectedNodes(selected)
      onSelect(selectedIdlist)
      transformerRef.current.nodes(selected);
      layerRef.current.draw(); // Redraw canvas
      setSelectionBox(null);
      setShapeType("")
      startPosRef.current = null;
      return
    }
    if (!drawing) return;
    const newShape = {
      id: `freehand-${Date.now()}`,
      type: 'freehand',
      props: {
        initialPoints: pointsRef.current,
        ...selectedShape?.props
      }
    };
    setShapes(prev => [...prev, newShape]);
    pointsRef.current = [];
    setLiveLine([]);
    setDrawing(false);
  };

  const handleSelect = (e) => {
    if (e.target === e.target.getStage()) {
      if (transformerRef.current) transformerRef.current.nodes([])
      setSelectedNodes([])
      return onSelect([]);
    }
    onSelect([e.target.id()]);
  };

  const onMoveHandleDragMove = (e) => {
    const dx = e.target.x() - moveHandleRef.current.startX;
    const dy = e.target.y() - moveHandleRef.current.startY;

    selectedNodes.forEach((node) => {
      node.x(node.x() + dx);
      node.y(node.y() + dy);
    });

    moveHandleRef.current.startX = e.target.x();
    moveHandleRef.current.startY = e.target.y();
  };

  const handleWheel = (e) => {
    e.evt.preventDefault();


    const stage = stageRef.current;
    if (!stage) return
    const oldScale = stage?.scaleX();

    const pointer = stage?.getPointerPosition();
    const scaleBy = 1.05; // zoom factor
    const newScale = e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };

    setScale(newScale);
    setPosition(newPos);
  };

  const handleDragEnd = (e) => {

    if (e.target === e.target.getStage()) {
      setPosition(e.target.position());
    }
  };

  useImperativeHandle(trigerRef, () => ({
    zoomStage,
    moveStage
  }));

  return (
    <div ref={containerRef} style={{ width: '100%', height: '74vh', display: 'flex', justifyContent: 'center' }}>
      <Stage
        ref={stageRef}
        width={scaled.width}
        height={scaled.height}
        onClick={handleSelect}
        onTap={handleSelect}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseUp={handleMouseUp}
        onDragEnd={handleDragEnd}
        scaleX={scale}
        scaleY={scale}
        x={position.x}
        y={position.y}
        style={{
          border: '3px solid #ffd80c', borderRadius: 18,
          boxSizing: 'border-box', overflow: 'hidden'
        }}
      >
        <Layer ref={layerRef}
        >
          {bgImage && (
            <KonvaImage ref={imageRef} image={bgImage}
              width={scaled.width}
              height={(scaled.height - 6)}
              x={0}
              y={0}
              listening={false}
              style={{ borderRadius: 12 }}
            />

          )}

          {/* Existing shapes */}
          {shapes.map((shape, idx) => {
            if (shape.deleted) return null;
            if (shape.hide) return null;
            const commonProps = {
              type: shape.type,
              key: shape.id,
              id: shape.id,
              draggable: true,
              x: shape.x,
              y: shape.y,
              initialX: shape.x,
              initialY: shape.y,
              width: 120,
              height: 120,
              isSelected: (selectedIds?.includes(shape.id) && selectedIds?.length == 1),
              onSelect: () => onSelect(shape.id),
              // stroke: "red",
              ...shape.props,
            };
            switch (shape.type) {
              case 'rectangle':
                return <BasicShapes {...commonProps} />;
              case 'triangle':
                return <BasicShapes  {...{ ...commonProps, type: 'polygon', points: [0, -80, 80, 80, -80, 80] }} />;
              case 'circle':
                return <BasicShapes {...commonProps} />;
              case 'polygon':
                return <BasicShapes {...commonProps} />;
              case 'arrow':
                return <Arrows  {...commonProps} type={shape.subType} />;
              case 'text':
                return <TextBox {...commonProps} />;
              case 'callout':
                return <Callout {...commonProps} />;
              case 'freehand':
                return <Freehand {...commonProps} />;
              default:
                return null;
            }
          })}

          {/* Live freehand drawing */}
          {drawing && (
            <>
              <Line
                points={liveLine}
                stroke={selectedShape?.props?.stroke}
                strokeWidth={selectedShape?.props?.strokeWidth}
                tension={0.5}
                lineCap="round"
                lineJoin="round"
                listening={false}
              />
            </>
          )}



        </Layer>

        <Layer>
          {Array.isArray(selectedIds) && selectedIds.length > 0 && (
            <Transformer
              ref={transformerRef}
              boundBoxFunc={(oldBox, newBox) => {
                if (newBox.width < 5 || newBox.height < 5) return oldBox;
                return newBox;
              }}
            />
          )}
        </Layer>

        <Layer>
          {selectionBox && (
            <Rect
              x={selectionBox.x}
              y={selectionBox.y}
              width={selectionBox.width}
              height={selectionBox.height}
              fill="rgba(0,161,255,0.3)"
              stroke="blue"
              dash={[4, 4]}
            />
          )}

          {(selectedNodes?.length > 0) && <Circle
            ref={moveHandleRef}
            skiable={true}
            x={transformerRef.current?.x() + transformerRef.current?.width() / 2}
            y={transformerRef.current?.y() + transformerRef.current?.height() / 2}
            radius={8}
            fill="blue"
            draggable
            onDragStart={(e) => {
              moveHandleRef.current.startX = e.target.x();
              moveHandleRef.current.startY = e.target.y();
            }}
            onDragMove={onMoveHandleDragMove}

            onMouseEnter={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = 'move'; // or 'move', 'crosshair', etc.
            }}
            onMouseLeave={(e) => {
              const container = e.target.getStage().container();
              container.style.cursor = 'default';
            }}
          />}
        </Layer>
      </Stage>
    </div>
  );
});
export default Canvas;